/** Pure helpers for click-to-move target snapping and soft collision recovery. */

export type TilePassableFn = (tx: number, ty: number) => boolean;
export type WorldPassableFn = (wx: number, wy: number) => boolean;

export function nearestWalkableTile(
  tx: number,
  ty: number,
  passable: TilePassableFn,
  maxRadius = 6
): { tx: number; ty: number } | null {
  if (passable(tx, ty)) return { tx, ty };
  for (let r = 1; r <= maxRadius; r++) {
    for (let dy = -r; dy <= r; dy++) {
      for (let dx = -r; dx <= r; dx++) {
        if (Math.abs(dx) !== r && Math.abs(dy) !== r) continue;
        const cx = tx + dx;
        const cy = ty + dy;
        if (passable(cx, cy)) return { tx: cx, ty: cy };
      }
    }
  }
  return null;
}

export function nearestPassableWorld(
  wx: number,
  wy: number,
  tileSize: number,
  passable: WorldPassableFn,
  maxRadiusTiles = 6
): { wx: number; wy: number } | null {
  if (passable(wx, wy)) return { wx, wy };
  const maxR = maxRadiusTiles * tileSize;
  let best: { wx: number; wy: number; d: number } | null = null;
  const step = Math.max(4, Math.floor(tileSize / 8));
  for (let dy = -maxR; dy <= maxR; dy += step) {
    for (let dx = -maxR; dx <= maxR; dx += step) {
      if (Math.abs(dx) + Math.abs(dy) > maxR) continue;
      const cx = wx + dx;
      const cy = wy + dy;
      if (!passable(cx, cy)) continue;
      const d = dx * dx + dy * dy;
      if (!best || d < best.d) best = { wx: cx, wy: cy, d };
    }
  }
  return best ? { wx: best.wx, wy: best.wy } : null;
}

export function slideToNearestPassable(
  wx: number,
  wy: number,
  lastWx: number,
  lastWy: number,
  tileSize: number,
  passable: TilePassableFn,
  maxRadius = 5,
  worldPassable?: WorldPassableFn
): { wx: number; wy: number; stuck: boolean } {
  if (!Number.isFinite(wx) || !Number.isFinite(wy)) {
    return { wx: lastWx, wy: lastWy, stuck: true };
  }
  if (worldPassable?.(wx, wy)) return { wx, wy, stuck: false };

  const tx = Math.floor(wx / tileSize);
  const ty = Math.floor(wy / tileSize);
  if (passable(tx, ty)) return { wx, wy, stuck: false };

  const dx = wx - lastWx;
  const dy = wy - lastWy;
  const len = Math.hypot(dx, dy);
  if (len > 0.5) {
    const steps = 6;
    for (let s = 1; s <= steps; s++) {
      const sx = wx - (dx / len) * (s / steps) * tileSize * 0.45;
      const sy = wy - (dy / len) * (s / steps) * tileSize * 0.45;
      if (worldPassable?.(sx, sy)) {
        return { wx: sx, wy: sy, stuck: false };
      }
      const stx = Math.floor(sx / tileSize);
      const sty = Math.floor(sy / tileSize);
      if (passable(stx, sty)) {
        return { wx: sx, wy: sy, stuck: false };
      }
    }
  }

  if (worldPassable) {
    const worldNear = nearestPassableWorld(wx, wy, tileSize, worldPassable, maxRadius);
    if (worldNear) return { wx: worldNear.wx, wy: worldNear.wy, stuck: false };
  }

  const near = nearestWalkableTile(tx, ty, passable, maxRadius);
  if (near) {
    return {
      wx: near.tx * tileSize + tileSize / 2,
      wy: near.ty * tileSize + tileSize / 2,
      stuck: false,
    };
  }
  return { wx: lastWx, wy: lastWy, stuck: true };
}
